import { lazy } from "react";

import LoginPage from "../component/pages/Auth/LoginPage";
import PasswordResetRequestPage from "../component/pages/Auth/PasswordResetRequestPage";
import PasswordResetVerifyPage from "../component/pages/Auth/PasswordResetVerifyPage";
import PasswordSetPage from "../component/pages/Auth/PasswordSetPage";
import DetailAccountPage from "../component/pages/Account/DetailAccountPage";
import UpdateAccountPage from "../component/pages/Account/UpdateAccountPage";
import SetPasswordPage from "../component/pages/Account/SetPasswordPage";
import SetCurrentBuildingPage from "../component/pages/Account/SetCurrentBuildingPage";

// 管理画面は基本不要なため遅延インポート
// システム管理者管理ページ
const ListSystemAdminPage = lazy(
  () => import("../component/pages/Admin/SystemAdmin/ListSystemAdminPage")
);
const CreateSystemAdminPage = lazy(
  () => import("../component/pages/Admin/SystemAdmin/CreateSystemAdminPage")
);
const DetailSystemAdminPage = lazy(
  () => import("../component/pages/Admin/SystemAdmin/DetailSystemAdminPage")
);
const UpdateSystemAdminPage = lazy(
  () => import("../component/pages/Admin/SystemAdmin/UpdateSystemAdminPage")
);
// テナント管理ページ
const ListTenantsAdminPage = lazy(
  () => import("../component/pages/Admin/Tenant/ListTenantsPage")
);
const CreateTenantPage = lazy(
  () => import("../component/pages/Admin/Tenant/CreateTenantPage")
);
const DetailTenantPage = lazy(
  () => import("../component/pages/Admin/Tenant/DetailTenantPage")
);
const UpdateTenantPage = lazy(
  () => import("../component/pages/Admin/Tenant/UpdateTenantPage")
);
// ロボット管理
const ListRobotPage = lazy(
  () => import("../component/pages/Admin/Robot/ListRobotPage")
);
const DetailRobotPage = lazy(
  () => import("../component/pages/Admin/Robot/DetailRobotPage")
);
const UpdateRobotPage = lazy(
  () => import("../component/pages/Admin/Robot/UpdateRobotPage")
);
// テナント内ユーザー管理ページ
const ListTenantUserPage = lazy(
  () => import("../component/pages/Admin/TenantUser/ListTenantUserPage")
);
const DetailTenantUserPage = lazy(
  () => import("../component/pages/Admin/TenantUser/DetailTenantUserPage")
);
const CreateTenantUserPage = lazy(
  () => import("../component/pages/Admin/TenantUser/CreateTenantUserPage")
);
const UpdateTenantUserPage = lazy(
  () => import("../component/pages/Admin/TenantUser/UpdateTenantUserPage")
);
// テナント内ロボット管理ページ
const ListTenantRobotPage = lazy(
  () => import("../component/pages/Admin/TenantRobot/ListTenantRobotPage")
);
const DetailTenantRobotPage = lazy(
  () => import("../component/pages/Admin/TenantRobot/DetailTenantRobotPage")
);
const UpdateTenantRobotPage = lazy(
  () => import("../component/pages/Admin/TenantRobot/UpdateTenantRobotPage")
);

export const APP_ROUTES = {
  public: {
    login: { path: "/auth/signin", component: LoginPage },
    passwordResetRequest: {
      path: "/auth/password-reset/request",
      component: PasswordResetRequestPage,
    },
    passwordResetVerify: {
      path: "/auth/password-reset/verify",
      component: PasswordResetVerifyPage,
    },
    passwordSet: { path: "/auth/password/set", component: PasswordSetPage },
  },
  authenticated: {
    root: { path: "/" },
  },
  me: {
    detail: {
      path: "/users/me",
      component: DetailAccountPage,
    },
    update: {
      path: "/users/me/edit",
      component: UpdateAccountPage,
    },
    setPassword: {
      path: "/users/me/password-edit",
      component: SetPasswordPage,
    },
    setCurrentBuilding: {
      path: "/users/me/set-currentBuilding",
      component: SetCurrentBuildingPage,
    },
  },
  systemAdminOnly: {
    // システム管理者管理
    createSystemAdmin: {
      path: "/admin/systemAdmin/new",
      component: CreateSystemAdminPage,
    },
    listSystemAdmin: {
      path: "/admin/systemAdmin",
      component: ListSystemAdminPage,
    },
    detailSystemAdmin: {
      path: "/admin/systemAdmin/:userId",
      route: (userId: string) => `/admin/systemAdmin/${userId}`,
      component: DetailSystemAdminPage,
    },
    updateSystemAdmin: {
      path: "/admin/systemAdmin/:userId/edit",
      route: (userId: string) => `/admin/systemAdmin/${userId}/edit`,
      component: UpdateSystemAdminPage,
    },
    // テナント管理
    listTenants: {
      path: "/admin/tenants",
      component: ListTenantsAdminPage,
    },
    createTenant: {
      path: "/admin/tenants/new",
      component: CreateTenantPage,
    },
    detailTenant: {
      path: "/admin/tenants/:tenantId",
      route: (tenantId: string) => `/admin/tenants/${tenantId}`,
      component: DetailTenantPage,
    },
    updateTenant: {
      path: "/admin/tenants/:tenantId/edit",
      route: (tenantId: string) => `/admin/tenants/${tenantId}/edit`,
      component: UpdateTenantPage,
    },
    // 全ロボット管理
    listRobot: {
      path: "/admin/robots",
      component: ListRobotPage,
    },
    detailRobot: {
      path: "/admin/robots/:robotId",
      route: (robotId: string) => `/admin/robots/${robotId}`,
      component: DetailRobotPage,
    },
    updateRobot: {
      path: "/admin/robots/:robotId/edit",
      route: (robotId: string) => `/admin/robots/${robotId}/edit`,
      component: UpdateRobotPage,
    },
    // テナント内ユーザー管理
    listTenantUser: {
      path: "/admin/tenants/:tenantId/users",
      route: (tenantId: string) => `/admin/tenants/${tenantId}/users`,
      component: ListTenantUserPage,
    },
    detailTenantUser: {
      path: "/admin/tenants/:tenantId/users/:userId",
      route: (tenantId: string, userId: string) =>
        `/admin/tenants/${tenantId}/users/${userId}`,
      component: DetailTenantUserPage,
    },
    createTenantUser: {
      path: "/admin/tenants/:tenantId/users/new",
      route: (tenantId: string) => `/admin/tenants/${tenantId}/users/new`,
      component: CreateTenantUserPage,
    },
    updateTenantUser: {
      path: "/admin/tenants/:tenantId/users/:userId/edit",
      route: (tenantId: string, userId: string) =>
        `/admin/tenants/${tenantId}/users/${userId}/edit`,
      component: UpdateTenantUserPage,
    },
    // テナント内ロボット管理
    listTenantRobot: {
      path: "/admin/tenants/:tenantId/robots",
      route: (tenantId: string) => `/admin/tenants/${tenantId}/robots`,
      component: ListTenantRobotPage,
    },
    detailTenantRobot: {
      path: "/admin/tenants/:tenantId/robots/:robotId",
      route: (tenantId: string, robotId: string) =>
        `/admin/tenants/${tenantId}/robots/${robotId}`,
      component: DetailTenantRobotPage,
    },
    updateTenantRobot: {
      path: "/admin/tenants/:tenantId/robots/:robotId/edit",
      route: (tenantId: string, robotId: string) =>
        `/admin/tenants/${tenantId}/robots/${robotId}/edit`,
      component: UpdateTenantRobotPage,
    },
  },
  tenantAdminOnly: {
    // テナント管理
    detailTenant: {
      path: "/admin/tenant",
      component: DetailTenantPage,
    },
    // テナント内ユーザー管理
    listTenantUser: {
      path: "/admin/tenant/users",
      component: ListTenantUserPage,
    },
    createTenantUser: {
      path: "/admin/tenant/users/new",
      component: CreateTenantUserPage,
    },
    detailTenantUser: {
      path: "/admin/tenant/users/:userId",
      route: (userId: string) => `/admin/tenant/users/${userId}`,
      component: DetailTenantUserPage,
    },
    updateTenantUser: {
      path: "/admin/tenant/users/:userId/edit",
      route: (userId: string) => `/admin/tenant/users/${userId}/edit`,
      component: UpdateTenantUserPage,
    },
    // テナント内ロボット管理
    listTenantRobot: {
      path: "/admin/tenant/robots",
      component: ListTenantRobotPage,
    },
    detailTenantRobot: {
      path: "/admin/tenant/robots/:robotId",
      route: (robotId: string) => `/admin/tenant/robots/${robotId}`,
      component: DetailTenantRobotPage,
    },
    updateTenantRobot: {
      path: "/admin/tenant/robots/:robotId/edit",
      route: (robotId: string) => `/admin/tenant/robots/${robotId}/edit`,
      component: UpdateTenantRobotPage,
    },
  },
} as const;
